/**
 * KSA Capability Map (P85.2) -- replaces the previous
 * `twin/landscape/KsaCapabilityMapView.tsx` per the
 * `design_handoff_capability_map/` handoff.
 *
 * Two lenses (Map / MVB) + Live cascade overlay + popover +
 * scrubber. Provenance per cell reuses the constellation's
 * ProvenanceTrace overlay (zero new plumbing).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Arrow } from "shared/components/Arrow";
import { useTranslation } from "react-i18next";
import { InfoTip } from "shared/components/Explainer";

import { FullscreenToggle } from "shared/layout/FullscreenToggle";
import { Twin3DStage, Twin3DToggle, useTwin3DMode } from "twin/twin3d/Twin3DMode";
import { useTwinGraph } from "twin/api/constellation_hooks";
import { useCascade } from "twin/api/hooks";
import { useTelemetry } from "twin/api/telemetry_hooks";
import { useCapabilityGraph } from "twin/capmap/hooks";
import { EmptyState } from "shared/components/EmptyState";
import { useMe } from "shared/api/useMe";
import { buildLiveStates } from "twin/capmap/liveStates";
import { CapabilityZoneCard } from "twin/capmap/CapabilityZoneCard";
import { CapMapScrubber, type ScrubberTick } from "twin/capmap/CapMapScrubber";
import { CascadeOverlay, type CellRect } from "twin/capmap/CascadeOverlay";
import { CapabilityPopover } from "twin/capmap/CapabilityPopover";
import { MetricsRail } from "twin/capmap/MetricsRail";
import { CapMapInputs } from "twin/capmap/CapMapInputs";
import { buildCascadeTree } from "twin/capmap/cascadeTree";
import { reachableDownstream } from "twin/capmap/litSet";
import {
  buildStateMap,
  pickIncidentOrigin,
  type CapabilityState,
} from "twin/capmap/stateMap";

import "twin/capmap/capability_map.css";

type Lens = "map" | "mvb" | "live";

const LENSES: { id: Lens; labelKey: string }[] = [
  { id: "map", labelKey: "modules.twin.capmap.lenses.map" },
  { id: "mvb", labelKey: "modules.twin.capmap.lenses.mvb" },
  { id: "live", labelKey: "modules.twin.capmap.lenses.live" },
];

/**
 * Round 11 C6 -- the walk-through, and what it is NOT.
 *
 * These read "09:00 baseline" through "09:48 channels degraded", and
 * the banner printed "as of 09:48" -- a clock time, on a supervisory
 * picture, that no reading in the estate supports. Every `asOf` here
 * is null: the ticks step a DEMONSTRATION of how a cascade spreads,
 * not a history, and nothing behind them is sealed at those moments.
 *
 * A fabricated timestamp is worse than a raw one. They are numbered
 * steps now, and the banner says which step rather than what time.
 */
// Round 14 B5 -- labels are catalogue KEYS, translated where drawn;
// they were English literals a commissioned Arabic build would have kept.
const SYNTHETIC_TICKS: ScrubberTick[] = [
  { label: "modules.twin.capmap.demoStep.baseline", asOf: null },
  { label: "modules.twin.capmap.demoStep.firstDegradation", asOf: null },
  { label: "modules.twin.capmap.demoStep.servicesFailing", asOf: null },
  { label: "modules.twin.capmap.demoStep.coreImpacted", asOf: null },
  { label: "modules.twin.capmap.demoStep.channelsDegraded", asOf: null },
];

const STEP_INTERVAL_MS = 1150;

export function KsaCapabilityMapView(): JSX.Element {
  const { t } = useTranslation();
  const graph = useCapabilityGraph();
  const cascade = useCascade();
  const [lens, setLens] = useState<Lens>("map");
  /* The figures are for everyone; only the route INTO the settings
     audit is admin-gated -- the ViewFigures rule. */
  const isAdmin = useMe().data?.is_admin ?? false;
  // P198 live lens: the twin topology (for IBS<->service edges) + the live
  // telemetry feed -- resolved to a per-cell live status only when lens=live.
  // Poll telemetry while the live lens is open so the board stays current.
  const twinGraph = useTwinGraph();
  const telemetry = useTelemetry(lens === "live");
  const [cascadeOn, setCascadeOn] = useState<boolean>(true);
  const [tickIndex, setTickIndex] = useState<number>(SYNTHETIC_TICKS.length - 1);
  const [playing, setPlaying] = useState<boolean>(false);
  /* Round 11 C6 -- the selection lives in the URL.
     
     A clicked cell changed the panel text and did not move the URL, so
     the one thing a reader wants to do with a supervisory picture --
     send somebody the cell they are looking at -- was impossible, and
     a reload lost it. Same fix the board's as-of control got this
     round, and for the same reason: a view worth reading is a view
     worth citing. */
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("cap");
  const setSelectedId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (cur) => {
          const next = new URLSearchParams(cur);
          if (id) next.set("cap", id);
          else next.delete("cap");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [popoverAnchor, setPopoverAnchor] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [rects, setRects] = useState<{
    map: Map<string, CellRect>;
    width: number;
    height: number;
  } | null>(null);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const {
    effectiveMode: cityEffective,
    setMode: setCityMode,
    onWebglFail: onCityWebglFail,
    loadFailed: cityLoadFailed,
  } = useTwin3DMode("city");

  const cells = useMemo(() => graph.data?.capabilities ?? [], [graph.data]);
  const zones = useMemo(() => graph.data?.zones ?? [], [graph.data]);
  const edges = useMemo(() => graph.data?.edges ?? [], [graph.data]);

  const states = useMemo<Map<string, CapabilityState>>(
    () => buildStateMap(cells, cascade.data, edges),
    [cells, cascade.data, edges],
  );

  // P198: the live-telemetry display states (worst supporting-service health
  // per capability). Built only under the live lens; the cascade machinery
  // (tree / metrics / overlay) keeps reading `states`, so the live lens only
  // re-tints the cells.
  const liveStates = useMemo<Map<string, CapabilityState>>(
    () =>
      lens === "live"
        ? buildLiveStates(
            cells,
            twinGraph.data?.edges ?? [],
            telemetry.data?.rows ?? [],
          )
        : new Map(),
    [lens, cells, twinGraph.data, telemetry.data],
  );
  const displayStates = lens === "live" ? liveStates : states;

  const originId = useMemo(
    () => pickIncidentOrigin(cells, cascade.data, edges),
    [cells, cascade.data, edges],
  );

  const treeEdges = useMemo(() => {
    if (!cascadeOn) return [];
    return buildCascadeTree({ cells, edges, states });
  }, [cascadeOn, cells, edges, states]);

  const litSet = useMemo<Set<string> | null>(
    () => (selectedId ? reachableDownstream(selectedId, edges) : null),
    [selectedId, edges],
  );

  const cellsByZone = useMemo(() => {
    const map = new Map<string, typeof cells>();
    /* Round 12 A6 -- a capability whose zone_code is neither null nor
       one of the twelve keyed on itself, so no zone card asked for it
       and it was drawn NOWHERE while still counting in "N capabilities
       mapped". The server already buckets those as unzoned when it
       decides whether to append the row; the grid agrees with it now,
       so the set drawn is the set counted. */
    const known = new Set(zones.map((z) => z.id));
    for (const c of cells) {
      const code = c.zone_code ?? "";
      const key = known.has(code) ? code : "unzoned";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    // Sort cells within each zone by name for stable rendering.
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return map;
  }, [cells, zones]);

  // Measure cell-centre rects relative to the overlay box.
  const measure = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const base = frame.getBoundingClientRect();
    const map = new Map<string, CellRect>();
    frame.querySelectorAll<HTMLElement>("[data-cap]").forEach((el) => {
      const id = el.getAttribute("data-cap");
      if (!id) return;
      const r = el.getBoundingClientRect();
      map.set(id, {
        x: r.left - base.left + r.width / 2,
        y: r.top - base.top + r.height / 2,
      });
    });
    setRects({ map, width: base.width, height: base.height });
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [lens, cells.length, measure]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (frameRef.current) ro.observe(frameRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  // Play loop -- advances through ticks at STEP_INTERVAL_MS.
  useEffect(() => {
    if (!playing) return;
    if (tickIndex >= SYNTHETIC_TICKS.length - 1) {
      setPlaying(false);
      return;
    }
    const id = window.setTimeout(() => {
      setTickIndex((s) => Math.min(s + 1, SYNTHETIC_TICKS.length - 1));
    }, STEP_INTERVAL_MS);
    return () => window.clearTimeout(id);
  }, [playing, tickIndex]);

  const onSelect = useCallback(
    (id: string, anchor: HTMLDivElement) => {
      if (selectedId === id) {
        setSelectedId(null);
        setPopoverAnchor(null);
        return;
      }
      setSelectedId(id);
      const stage = stageRef.current?.getBoundingClientRect();
      const r = anchor.getBoundingClientRect();
      if (!stage) return;
      setPopoverAnchor({
        x: Math.min(r.left - stage.left + r.width + 8, stage.width - 280),
        y: Math.max(8, r.top - stage.top - 4),
      });
    },
    // `setSelectedId` belongs here. C6 moved the selection from
    // useState into the URL so a cell could be cited, and that turned
    // it from a React setter -- which the exhaustive-deps rule exempts
    // because its identity never changes -- into a useCallback, which
    // it does not exempt. It is stable in practice (it closes over
    // react-router's `setSearchParams`), so naming it changes no
    // behaviour; leaving it out left two warnings, and CI lints at
    // --max-warnings 0.
    [selectedId, setSelectedId],
  );

  const closePopover = useCallback(() => {
    setSelectedId(null);
    setPopoverAnchor(null);
  }, [setSelectedId]);

  /* ROUND 13 D4 -- the popover reported a dependency COUNT, and a
     count is not an edge. Ten capability dependency edges were
     committed and no register, drawer, map or scene showed one, so a
     number here answered "how many" and left "which" nowhere on the
     product. The names are already on the client; nothing was joining
     them. */
  const dependencyNames = useMemo(() => {
    if (!selectedId) return [];
    const byId = new Map(cells.map((c) => [c.id, c.name] as const));
    return edges
      .filter((e) => e.dependent_id === selectedId)
      .map((e) => byId.get(e.upstream_id) ?? e.upstream_id)
      .sort((a, b) => a.localeCompare(b));
  }, [selectedId, edges, cells]);

  const selectedCell = useMemo(
    () => cells.find((c) => c.id === selectedId) ?? null,
    [cells, selectedId],
  );

  if (graph.isError) {
    return (
      <article className="m-6 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <strong className="block font-semibold">
          {t("modules.twin.capmap.error")}
        </strong>
        {graph.error.detail}
      </article>
    );
  }

  if (graph.isPending) {
    return (
      <article className="m-6 text-sm text-slate-500" data-testid="capmap-loading">
        {t("modules.twin.capmap.loading")}
      </article>
    );
  }

  const empty = cells.length === 0;
  const unzonedCount = cellsByZone.get("unzoned")?.length ?? 0;
  const tickLabel = t(SYNTHETIC_TICKS[tickIndex]?.label ?? "");
  // Round 14 B5 -- "5 -- channels degraded" rested on an estate with no
  // telemetry and nothing assessed. On such an estate the walk-through
  // demonstrates nothing about THIS bank, so it is not offered; where it
  // is offered, it says it is a demonstration.
  const unassessed = cascade.data?.estate_assessed === false;

  // The capability city is the 3D form of THIS surface -- its view id
  // and its storage key (`marsad.twin.capmap.viewMode`) were declared
  // for the capmap from the start. The scene, its inspector, its
  // legend and its labels all shipped; nothing ever mounted them, so
  // the whole view was reachable from nowhere until the round 10
  // measurement pass derived the scene list from source instead of
  // trusting it. `tests/measured_surfaces.test.ts` is the ratchet.
  // `effectiveMode`, NOT `mode`: WebGL can fail or be unavailable, and
  // the hook falls the render back to 2D without destroying what the
  // operator picked. Gating on `mode` would strand them on a stage
  // that cannot draw.
  if (cityEffective === "3d") {
    return (
      /* Round 16 E12 -- the 2D map's five-row grid put the scene in an
         auto row, so it took its minimum height and left a blank band
         under it. The scene gets the constellation's frame instead: a
         column the stage fills. */
      <article
        ref={rootRef}
        data-testid="capmap-view"
        className="capmap-app capmap-app--scene"
      >
        <header className="capmap-tbar">
          <span className="capmap-crumb">
            {t("modules.twin.capmap.crumb")} · <b>{t("modules.twin.capmap.heading")}</b>
            {/* ROUND 13 H6 -- the construct, where it is met. A reader
                arriving on the capability city for the first time had
                no entry anywhere for what one is. */}
            <InfoTip termKey="capability_city" tone="dark" />
          </span>
          <Twin3DToggle
            mode={cityEffective}
            onChange={setCityMode}
            testid="capmap-view"
            loadFailed={cityLoadFailed}
          />
          <FullscreenToggle targetRef={rootRef} />
        </header>
        <Twin3DStage view="city" onWebglFail={onCityWebglFail} />
      </article>
    );
  }

  return (
    <article
      ref={rootRef}
      data-testid="capmap-view"
      className="capmap-app"
      onClick={closePopover}
    >
      <header className="capmap-tbar">
        <span className="capmap-crumb">
          {t("modules.twin.capmap.crumb")} · <b>{t("modules.twin.capmap.heading")}</b>
        </span>
        <div data-testid="capmap-lens-switcher" className="capmap-seg">
          {LENSES.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`capmap-lens-${l.id}`}
              data-active={lens === l.id ? "true" : "false"}
              className={lens === l.id ? "capmap-on" : ""}
              onClick={(e) => {
                e.stopPropagation();
                setLens(l.id);
              }}
            >
              {t(l.labelKey)}
            </button>
          ))}
        </div>
        <label
          data-testid="capmap-cascade-toggle"
          className={"capmap-tgl" + (cascadeOn ? " capmap-tgl-on" : "")}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={cascadeOn}
            onChange={(e) => setCascadeOn(e.target.checked)}
            className="sr-only"
          />
          <span className="capmap-tgl-sw" />
          {t("modules.twin.capmap.cascadeToggle")}
        </label>
        <Twin3DToggle
          mode={cityEffective}
          onChange={setCityMode}
          testid="capmap-view"
          loadFailed={cityLoadFailed}
        />
        <FullscreenToggle targetRef={rootRef} />
      </header>

      <MetricsRail cells={cells} states={states} cascade={cascade.data} />

      {/* Round 11 C5 -- directly under the tiles it explains, because
          the tiles are what a reader is being asked to believe. */}
      <CapMapInputs lens={lens} isAdmin={isAdmin} />

      {/* Round 12 A6 -- the walk met "2 CAPABILITIES MAPPED" printed
          over twelve zeroes. Both capabilities existed; neither carried
          a zone, so both sat under Unzoned and every zone on the map
          was honestly empty. Nothing said so, and a map that explains
          none of its own emptiness reads as broken. */}
      {!empty && unzonedCount > 0 && (
        <div data-testid="capmap-unzoned" className="capmap-unzoned">
          <span>{t("modules.twin.capmap.unzoned", { count: unzonedCount })}</span>
          {isAdmin && (
            <Link to="/foundation/admin/capabilities">
              {t("modules.twin.capmap.unzonedCta")} <Arrow />
            </Link>
          )}
        </div>
      )}

      {cascadeOn && originId && (
        <div data-testid="capmap-incident-banner" className="capmap-incbar">
          <span className="capmap-incid" />
          <b>{t("modules.twin.capmap.incidentBanner.title")}</b>
          <span className="capmap-incsrc">
            {t("modules.twin.capmap.incidentBanner.asOf", {
              tick: tickLabel,
            })}
          </span>
        </div>
      )}

      <div ref={stageRef} data-testid="capmap-stage" className="capmap-stage">
        {empty ? (
          /* Round 19 H56 -- the empty state was laid absolutely over the
             stage, and the banner above covered its sentence and its
             "Load capabilities". In the page's own flow, nothing can sit
             on it. */
          <div
            data-testid="capmap-empty-wrap"
            className="flex min-h-[240px] items-center justify-center p-6"
          >
            <EmptyState
              testid="capmap-empty"
              tone="dark"
              message={
                t("modules.twin.capmap.empty") +
                (isAdmin ? "" : " " + t("common.emptyAskAdmin"))
              }
              cta={
                isAdmin
                  ? {
                      label: t("modules.twin.capmap.emptyCta"),
                      to: "/admin/data-import?kind=capability",
                    }
                  : undefined
              }
            />
          </div>
        ) : (
          <div
            ref={frameRef}
            data-testid="capmap-frame"
            data-lens={lens}
            className={
              `capmap-mapframe capmap-lens-${lens}` + (litSet ? " capmap-focus" : "")
            }
          >
            {zones.map((z) => (
              <CapabilityZoneCard
                key={z.id}
                zone={z}
                cells={cellsByZone.get(z.id) ?? []}
                states={displayStates}
                selectedId={selectedId}
                litSet={litSet}
                lens={lens}
                originId={originId}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {rects && cascadeOn && treeEdges.length > 0 && (
          <CascadeOverlay
            rects={rects.map}
            width={rects.width}
            height={rects.height}
            treeEdges={treeEdges}
            originId={originId}
            litSet={litSet}
            selectedId={selectedId}
          />
        )}

        {selectedCell && popoverAnchor && (
          <CapabilityPopover
            cap={selectedCell}
            state={displayStates.get(selectedCell.id) ?? "stable"}
            anchor={popoverAnchor}
            isOrigin={selectedCell.id === originId}
            dependencyNames={dependencyNames}
            onClose={closePopover}
          />
        )}
      </div>

      {/* A6 -- the five-step walk-through is a DEMONSTRATION of how a
          cascade spreads across the grid (C6 numbered its steps rather
          than dating them). With no grid it demonstrates nothing, and
          a "5 -- channels degraded" strip over an estate carrying no
          telemetry is a claim about that estate. */}
      {!empty && !unassessed && (
        <CapMapScrubber
          ticks={SYNTHETIC_TICKS.map((tick) => ({ ...tick, label: t(tick.label) }))}
          selectedIndex={tickIndex}
          playing={playing}
          onSelect={(i) => {
            setPlaying(false);
            setTickIndex(i);
          }}
          onTogglePlay={() => {
            if (tickIndex >= SYNTHETIC_TICKS.length - 1) setTickIndex(0);
            setPlaying((p) => !p);
          }}
        />
      )}
    </article>
  );
}
